import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React from "react";
import { WithIntlProps, injectIntl } from "react-intl";
import { RequiredCandidateMyProfile, Switzerland } from "../../../data/constants";
import ZipCodes from "../../../data/zipCodes.json";
import { CandidateViewModel } from "../../../model/CandidateViewModel";
import { GetTranslation } from "../../../translations/GetTranslation";
import FormGridCell from "../../common/FormGridCell";
import { CountrySelect } from "../../common/customAutocomplete/CountrySelect";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { isInvalidZipCode } from "../../../utils/ZipCodeUtils";

type CandidateBusinessFieldsProps = {
  candidate: CandidateViewModel;
  isRequired: RequiredCandidateMyProfile;
  onPropertyValueChanges?: (part: Partial<CandidateViewModel>) => void;
} & WithIntlProps<any>;

const CandidateBusinessFields: React.FunctionComponent<CandidateBusinessFieldsProps> = ({
  candidate,
  intl,
  isRequired,
  ...handlers
}) => {
  const handleTextChange =
    (key: keyof CandidateViewModel) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      handleChange(key, event.target.value);
  const handleSelectChange = (key: keyof CandidateViewModel) => (value?: string) => handleChange(key, value);
  const handleChange = (key: keyof CandidateViewModel, value?: string | string[] | Date | boolean) => {
    let part: Partial<CandidateViewModel> = {
      [key]: value,
    };

    if (handlers.onPropertyValueChanges) {
      handlers.onPropertyValueChanges(part);
    }
  };
  const handleZipCodeChange =
    (zipCodeKey: keyof CandidateViewModel, cityKey: keyof CandidateViewModel) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChange(zipCodeKey, event.target.value);
        handleChange(cityKey, "");
        ZipCodes.forEach((zipCode) => {
          if (zipCode.zipCode === event.target.value) {
            handleChange(cityKey, zipCode.city);
          }
        });
      };

  if (candidate.employerCountry === "") {
    handleChange("employerCountry", Switzerland);
  }

  return (
    <>
      <Typography variant="h6">
        <TranslatedMessage id="person.section.businessData" />
      </Typography>
      <Grid container={true} spacing={4}>
        <Grid item container xs={12}>
          <FormGridCell size="third">
            <TextField
              fullWidth={true}
              error={isRequired.isEmployerEmpty}
              required
              label={GetTranslation(intl, "person.field.employer")}
              onChange={handleTextChange("employer")}
              value={candidate.employer || ""}
            />
          </FormGridCell>
        </Grid>
        <FormGridCell size="fourth">
          <TextField
            fullWidth={true}
            error={isRequired.isEmployerStreetEmpty}
            required
            label={GetTranslation(intl, "person.field.employerStreet")}
            onChange={handleTextChange("employerStreet")}
            value={candidate.employerStreet || ""}
          />
        </FormGridCell>
        <FormGridCell size="sixth">
          <TextField
            fullWidth={true}
            error={isRequired.isEmployerStreetNumberEmpty}
            required
            label={GetTranslation(intl, "person.field.employerStreetNumber")}
            onChange={handleTextChange("employerStreetNumber")}
            value={candidate.employerStreetNumber || ""}
          />
        </FormGridCell>
        <FormGridCell size="sixth">
          <TextField
            fullWidth={true}
            error={isRequired.isEmployerZipCodeEmpty || isInvalidZipCode(candidate.employerZipCode, candidate.employerCountry)}
            required
            label={GetTranslation(intl, "person.field.employerZipCode")}
            onChange={handleZipCodeChange("employerZipCode", "employerCity")}
            value={candidate.employerZipCode || ""}
          />
        </FormGridCell>
        <FormGridCell size="sixth">
          <TextField
            fullWidth={true}
            error={isRequired.isEmployerCityEmpty}
            required
            label={GetTranslation(intl, "person.field.employerCity")}
            onChange={handleTextChange("employerCity")}
            value={candidate.employerCity || ""}
          />
        </FormGridCell>
        <CountrySelect
          xs={12}
          sm={6}
          md={3}
          label={GetTranslation(intl, "person.field.country")}
          value={candidate.employerCountry}
          onChange={handleSelectChange("employerCountry")}
          required
          error={isRequired.isEmployerCountryEmpty}
        />
      </Grid>
    </>
  );
};

CandidateBusinessFields.defaultProps = {
  onPropertyValueChanges: () => { },
} as Partial<CandidateBusinessFieldsProps>;

export default injectIntl(CandidateBusinessFields);
